import { getVendorSocket } from "@/infra/sockets";
import SubmitQuestionnaireModal from "@/modules/agent/components/VendorEmail/SubmitQuestionnaireModal";
import VendorEmailPlaceHolder from "@/modules/agent/components/VendorEmail/VendorEmailSuccess";
import GuestSidebar from "@/modules/app/components/sidebar/GuestSidebar";
import { ZButton } from "@/shared/components/button";
import ZBreadCrumbs from "@/shared/components/custom/breadcrumbs";
import { LoadingSpinner } from "@/shared/components/custom/spin";
import { useAdvancedState } from "@/shared/hooks/useAdvancedState";
import { addNotification } from "@/shared/states/notification";
import { useSocketActions, useSocketData } from "@/shared/states/sockets";
import { UploadedFile } from "@/shared/types/file-upload";
import { cn } from "@/shared/utils/classname-merger";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { EvidenceDocument, fetchQuestionnaire, PatchEvidenceResponse, useGetChatMessages, useGetEvidenceCollectionAuth, useGetVendorSessionQuestionnaire, usePatchEvidenceDocument, useSubmitQuestionnaireForm, useSubmitVendorIntakeForm, useUploadEvidenceDocuments } from "../requests";
import { ChatMessage, ChatMessageReceivedPayload, MessageRole, Question, VendorSessionQuestionnaire } from "../types";
import ThirdPartyFormExpiredScreen from "./ExpiredScreen";
import SuccessScreen from "./SuccessScreen";
import AssessmentLoadingScreen from "./components/AssessmentLoadingScreen";
import DocumentUploadGrid from "./components/DocumentUploadGrid";
import QuestionsForm from "./components/QuestionsForm";
import ThirdPartyHeader, { TabType } from "./components/ThirdPartyHeader";

export const ThirdPartyVendorLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <main className="h-screen relative w-screen flex">
      <section
        className={cn("flex-[5]", "transition-all duration-300 ease-in-out",
        )}
      >
        <GuestSidebar />
      </section>
      <section
        className={cn("flex-[95]", "overflow-hidden overflow-y-auto p-4 ",
        )}
      >
        <div className="overflow-hidden rounded-lg border-none w-full h-full">
          {children}
        </div>
      </section>
    </main>
  );
}

const VendorIntakeForm = () => {

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const vendorToken = queryParams.get('token') as string;

  const {
    state: { isLoading, data, isSessionExpired, isAlreadySubmitted, source_urls, isConfirmModalOpen, evidenceDocuments, documentFiles, uploadErrors, savingStates, totalRequiredQuestions, completedRequiredQuestions, isLoadingQuestions, vendorQuestionnaire, shouldShowChatScreen, chatMessages, isLoadingChatMessages, showAgentWaiting, isWaitingForFollowUp, isFormAlreadySubmitted },
    updateState,
    toggleField
  } = useAdvancedState<any>({
    isLoading: true,
    data: {},
    isSessionExpired: false,
    isAlreadySubmitted: false,
    source_urls: [],
    isConfirmModalOpen: false,
    evidenceDocuments: [] as EvidenceDocument[],
    documentFiles: {} as Record<string, UploadedFile[]>,
    uploadErrors: {} as Record<string, boolean>,
    savingStates: {} as Record<string, boolean>,
    totalRequiredQuestions: 0,
    completedRequiredQuestions: 0,
    isLoadingQuestions: false,
    vendorQuestionnaire: null as VendorSessionQuestionnaire | null,
    shouldShowChatScreen: false,
    chatMessages: [] as ChatMessage[],
    isLoadingChatMessages: false,
    showAgentWaiting: false,
    isWaitingForFollowUp: false,
    isFormAlreadySubmitted: false,
  });

  const [activeTab, setActiveTab] = useState<TabType>('documents');
  const [questionsPagination, setQuestionsPagination] = useState<React.ReactNode>(null);
  const [formKey, setFormKey] = useState(0);

  // Ref to track current token and prevent stale API responses from old tokens
  const currentTokenRef = useRef<string>(vendorToken);
  // Ref to track if we've already fetched questionnaire for current token
  const hasFetchedQuestionnaireRef = useRef<boolean>(false);

  const { mutate: fetchEvidenceData, isPending: isEvidenceLoading } = useGetEvidenceCollectionAuth();
  const { mutate: fetchVendorQuestionnaire, isPending: isLoadingVendorQuestionnaire } = useGetVendorSessionQuestionnaire();
  const { mutate: fetchChatMessages, isPending: isLoadingChatMessagesRequest } = useGetChatMessages();
  const { mutate: uploadEvidence, isPending: isUploading } = useUploadEvidenceDocuments();
  const { mutate: patchEvidence, isPending: isPatchingEvidence } = usePatchEvidenceDocument();

  // Socket setup
  const { addSocket, removeSocket } = useSocketActions();
  const existingSocketData = useSocketData('vendorForm');

  const initializeSocket = useCallback((token?: string) => {
    try {
      const socket = getVendorSocket(token);

      if (socket) {
        // Add socket to the global socket store for potential use by other components
        addSocket('vendorForm', {
          socket,
          isConnected: socket.connected,
          lastUpdatedTimestamp: Date.now(),
          isErrored: false
        });

        // Update connection status when socket connects/disconnects
        const handleConnect = () => {
          addSocket('vendorForm', {
            socket,
            isConnected: true,
            lastUpdatedTimestamp: Date.now(),
            isErrored: false
          });
        };

        const handleDisconnect = (reason: string) => {
          addSocket('vendorForm', {
            socket,
            isConnected: false,
            lastUpdatedTimestamp: Date.now(),
            isErrored: false
          });
        };

        const handleError = (error: any) => {
          addSocket('vendorForm', {
            socket,
            isConnected: false,
            lastUpdatedTimestamp: Date.now(),
            isErrored: true
          });
        };

        // Register event listeners
        socket.on('connect', handleConnect);
        socket.on('disconnect', handleDisconnect);
        socket.on('error', handleError);

        // Listen for chat message responses
        const handleChatMessageReceived = (data: ChatMessageReceivedPayload) => {
          updateState((currentState: any) => {
            if (currentTokenRef.current !== token) {
              return {}; // No state update
            }

            // Extract message from nested structure
            const message = data.message;
            
            // Validate required fields in the nested message object
            if (!message || !message.text || !message.role || !message.chat_id) {
              return {}; // No state update
            }

            // Check if this message is for the current chat session (use message.chat_id)
            if (currentState.vendorQuestionnaire?.chat_id !== message.chat_id) {
              return {}; // No state update
            }

            // Normalize role (handle Python enum values)
            let rawRole: string = message.role as string;
            if (typeof message.role === 'object' && 'value' in message.role) {
              // Handle Python enum format: <MessageRole.USER: 'user'>
              rawRole = message.role.value as string;
            }
            
            // Convert to proper MessageRole
            const role: MessageRole = rawRole === 'agent' ? 'assistant' : rawRole as MessageRole;

            // Create a new chat message object from the received data
            const newMessage: ChatMessage = role === 'assistant' ? {
              id: message.id || `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              chat_id: message.chat_id,
              role: 'assistant',
              type: 'text',
              created_at: message.created_at || new Date().toISOString(),
              text: message.text
            } : {
              id: message.id || `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              chat_id: message.chat_id,
              role: 'user',
              type: 'text',
              created_at: message.created_at || new Date().toISOString(),
              text: message.text,
              attachments: message.attachments || []
            };

            // Check for duplicate messages (based on ID first, then content and timestamp)
            const existingMessages = currentState.chatMessages || [];
              const isDuplicate = existingMessages.some((msg: ChatMessage) => 
                (msg.id === newMessage.id) || // Check by ID first
                (msg.text === newMessage.text && 
                 msg.role === newMessage.role &&
                 Math.abs(new Date(msg.created_at).getTime() - new Date(newMessage.created_at).getTime()) < 1000) // Within 1 second
              );
              
              if (isDuplicate) {
                return {}; // No state update
              }

              // Add the new message to the chat messages
              return {
                ...currentState,
                chatMessages: [...existingMessages, newMessage]
              };
          });
        };

        socket.on('chat_message_received', handleChatMessageReceived);

        // Listen for follow-up questions generated event
        const handleFollowUpQuestionsGenerated = (data: any) => {
          if (currentTokenRef.current !== token) {
            return;
          }

          // Refetch vendor questionnaire to get updated status
          if (data.agent_session_id) {
            fetchVendorQuestionnaire(undefined, {
              onSuccess: (questionnaireResponse) => {
                // Check token again before updating state
                if (currentTokenRef.current !== token) {
                  return;
                }

                const questionnaire = questionnaireResponse.questionnaire;
                const shouldShowChat = questionnaire.questionnaire_status === 'followup_questions_generated';
                const shouldShowAgentWaiting = questionnaire.questionnaire_status === 'completed';
                
                updateState({
                  vendorQuestionnaire: questionnaire,
                  shouldShowChatScreen: shouldShowChat,
                  showAgentWaiting: shouldShowAgentWaiting,
                  isWaitingForFollowUp: false,
                });

                // If we should show chat, fetch the chat messages
                if (shouldShowChat && questionnaire.chat_id) {
                  updateState({ isLoadingChatMessages: true });
                  fetchChatMessages(
                    { chatId: questionnaire.chat_id },
                    {
                      onSuccess: (chatResponse) => {
                        // Check token again before updating
                        if (currentTokenRef.current !== token) {
                          return;
                        }
                        updateState({
                          chatMessages: chatResponse.messages || [],
                          isLoadingChatMessages: false,
                        });
                      },
                      onError: (error) => {
                        if (currentTokenRef.current === token) {
                          updateState({ isLoadingChatMessages: false });
                        }
                      }
                    }
                  );
                }
              },
              onError: (error) => {
                // Error handled
              }
            });
          }
        };

        socket.on('tpra:follow_up:questions:generated', handleFollowUpQuestionsGenerated);

        // Listen for streaming AI message events
        const handleStreamingAIMessage = (data: any) => {
          
          // Use updateState with callback to access current state
          updateState((currentState: any) => {
            if (currentTokenRef.current !== token) {
              return {}; // No state update
            }

            // Check if this message is for the current chat session
            if (!data.chat_id || currentState.vendorQuestionnaire?.chat_id !== data.chat_id) {
              return {}; // No state update
            }

            const existingMessages = currentState.chatMessages || [];
            let updatedMessages = [...existingMessages];

            switch (data.event) {
              case 'ai_message_start':
                // Create a new assistant message with initial content
                const newAssistantMessage: ChatMessage = {
                  id: data.message_id || `ai_msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                  chat_id: data.chat_id,
                  role: 'assistant',
                  type: 'text',
                  created_at: new Date().toISOString(),
                  text: data.message || ''
                };
                updatedMessages.push(newAssistantMessage);
                break;
                
              case 'ai_message_delta':
                // Find the last assistant message and append to it
                const lastMessageIndex = updatedMessages.length - 1;
                if (lastMessageIndex >= 0 && updatedMessages[lastMessageIndex].role === 'assistant') {
                  updatedMessages[lastMessageIndex] = {
                    ...updatedMessages[lastMessageIndex],
                    text: updatedMessages[lastMessageIndex].text + (data.message || '')
                  };
                } else {
                  // If no assistant message exists, create one (fallback)
                  const deltaMessage: ChatMessage = {
                    id: data.message_id || `ai_msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    chat_id: data.chat_id,
                    role: 'assistant',
                    type: 'text',
                    created_at: new Date().toISOString(),
                    text: data.message || ''
                  };
                  updatedMessages.push(deltaMessage);
                }
                break;
                
              case 'ai_message_end':
                // Finalize the assistant message with complete content
                const finalMessageIndex = updatedMessages.length - 1;
                if (finalMessageIndex >= 0 && updatedMessages[finalMessageIndex].role === 'assistant') {
                  updatedMessages[finalMessageIndex] = {
                    ...updatedMessages[finalMessageIndex],
                    text: data.message || updatedMessages[finalMessageIndex].text,
                    id: data.message_id || updatedMessages[finalMessageIndex].id
                  };
                } else {
                  // If no assistant message exists, create the final one
                  const finalMessage: ChatMessage = {
                    id: data.message_id || `ai_msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    chat_id: data.chat_id,
                    role: 'assistant',
                    type: 'text',
                    created_at: new Date().toISOString(),
                    text: data.message || ''
                  };
                  updatedMessages.push(finalMessage);
                }
                break;
                
              default:
                return {}; // No state update
            }

            return {
              ...currentState,
              chatMessages: updatedMessages
            };
          });
        };

        socket.on('tpra:followup:chat:ai:message', handleStreamingAIMessage);

        // Listen for questions submitted event (keeps multiple tabs in sync)
        const handleQuestionsSubmitted = (data: any) => {
          updateState((currentState: any) => {
            // Check token first
            if (currentTokenRef.current !== token) {
              return {}; // No state update if token changed
            }

            // Show loading screen immediately
            const updatedState = {
              isWaitingForFollowUp: true
            };

            // Force form to re-render to show updated responses
            setFormKey(prev => prev + 1);

            return updatedState;
          });
        };        // Listen for questionnaire completed event (keeps multiple tabs in sync)
        const handleQuestionnaireCompleted = (data: any) => {
          updateState((currentState: any) => {
            if (currentTokenRef.current !== token) {
              return {}; // No state update if token changed
            }

            // Update state to show success screen
            return {
              isFormAlreadySubmitted: true,
              data: {
                ...currentState.data,
                questionnaire_completion_status: true,
              }
            };
          });
        };

        socket.on('tpra:vendor:questions:submitted', handleQuestionsSubmitted);
        socket.on('tpra:vendor:questionnaire:completed', handleQuestionnaireCompleted);

        // Force connection attempt if not already connected
        if (!socket.connected) {
          socket.connect();
        }
      }
    } catch (error) {
      // Socket initialization failed
    }
  }, [addSocket]);

  const fetchVendorFormDetails = async (token: string) => {
    try {
      // Call authenticate API directly - this replaces the validate-token call
      // and sets HTTP cookies for subsequent requests
      fetchEvidenceData(token, {
        onSuccess: (evidenceResponse) => {
          // Check if authentication was successful
          if (!evidenceResponse.token_valid) {
            updateState({
              isSessionExpired: true,
              isLoading: false
            });
            return;
          }

          // Initialize socket connection AFTER successful authentication with a longer delay
          // This ensures the HTTP cookies are set and server is ready before socket connection
          // Increased delay due to socket connection issues on first load
          setTimeout(() => {
            initializeSocket(token);
          }, 2000);

          // Convert existing source_urls to UploadedFile format
          const documentFiles: Record<string, UploadedFile[]> = {};

          if (evidenceResponse.evidence_documents) {
            evidenceResponse.evidence_documents.forEach((doc) => {
              if (doc.source_urls && doc.source_urls.length > 0) {
                documentFiles[doc.id] = doc.source_urls.map((url, index) => ({
                  id: `existing-${doc.id}-${index}`,
                  file: new File([], doc.file_names[index]),
                  preview: "",
                  status: "success" as const,
                  progressBar: false,
                  type: "",
                  signedUrl: url
                }));
              }
            });
          }

          const questionnaireUrl = evidenceResponse.questionnaire_json_url;
          const completionStatus = evidenceResponse.questionnaire_completion_status;

          // Update state with evidence data but keep loading until we determine the screen to show
          updateState({
            evidenceDocuments: evidenceResponse.evidence_documents || [],
            documentFiles,
            data: {
              success: evidenceResponse.success,
              questionnaire_json_url: questionnaireUrl,
              questionnaire_excel_url: evidenceResponse.questionnaire_excel_url,
              organization_name: evidenceResponse.organization_name,
              vendor_name: evidenceResponse.vendor_name,
              assessor_name: evidenceResponse.assessor_name,
              contact_email: evidenceResponse.requester_email,
              questionnaire_completion_status: completionStatus,
            },
            isSessionExpired: false,
            // Keep isLoading true - will be set to false in the second useEffect after we have questionnaire status
          });

          // Fetch questions data in background for progress calculation (only for pending status)
          if (questionnaireUrl && !completionStatus) {
            updateState({ isLoadingQuestions: true });

            fetchQuestionnaire(questionnaireUrl)
              .then((questions: Question[]) => {
                // Calculate questions progress
                const requiredQuestions = questions.filter(question => question.required);
                const totalRequired = requiredQuestions.length;

                // Count completed required questions based on existing response_value
                const completedRequired = requiredQuestions.filter(question => {
                  const response = question.response_value;

                  // Check if question is completed based on its type
                  if (question.question_type === 'multiple_choice') {
                    return Array.isArray(response) && response.length > 0;
                  } else {
                    // For 'free_text' and 'single_choice'
                    return typeof response === 'string' && response.trim().length > 0;
                  }
                }).length;

                // Update questions progress in state
                updateState({
                  totalRequiredQuestions: totalRequired,
                  completedRequiredQuestions: completedRequired,
                  isLoadingQuestions: false,
                  isLoading: false, // Hide loader now for normal questionnaire flow
                });
              })
              .catch((error) => {

                updateState({ 
                  isLoadingQuestions: false,
                  isLoading: false, // Hide loader even on error
                });
                // Don't show user error for this background operation
              });
          }
        },
        onError: (error) => {
          // Handle errors based on status codes
          const status = (error as any)?.response?.status || (error as any)?.status;
          // Extract organization name from error object (set by getEvidenceCollectionAuth)
          const organizationName = (error as any)?.organization_name;
          
          if (status === 403) {
            // 403 -> Form already submitted (response sent)
            updateState({
              isFormAlreadySubmitted: true,
              isLoading: false,
              // Store organization name in data object if available from error
              data: organizationName ? {
                ...data,
                organization_name: organizationName
              } : data
            });
          } else if (status === 401) {
            // 401 -> Invalid link/token
            updateState({
              isSessionExpired: true,
              isLoading: false
            });
          } else {
            // Any other error -> Invalid link screen
            updateState({
              isSessionExpired: true,
              isLoading: false
            });
            addNotification({
              message: "Failed to load form details. Please check your link or contact support.",
              type: "error",
            });
          }
        }
      });
    } catch (error) {
      updateState({
        isSessionExpired: true,
        isLoading: false
      });
    }
  }

  useEffect(() => {
    // Update current token ref FIRST to track the latest token
    currentTokenRef.current = vendorToken;
    // Reset questionnaire fetch flag for new token
    hasFetchedQuestionnaireRef.current = false;

    // Clean up old socket connection when token changes
    if (existingSocketData?.socket) {
      existingSocketData.socket.disconnect();
      removeSocket('vendorForm');
    }

    // Reset state when token changes to prevent data from previous token persisting
    updateState({
      isLoading: true,
      data: {},
      isSessionExpired: false,
      isAlreadySubmitted: false,
      source_urls: [],
      isConfirmModalOpen: false,
      evidenceDocuments: [],
      documentFiles: {},
      uploadErrors: {},
      savingStates: {},
      totalRequiredQuestions: 0,
      completedRequiredQuestions: 0,
      isLoadingQuestions: false,
      vendorQuestionnaire: null,
      shouldShowChatScreen: false,
      chatMessages: [],
      isLoadingChatMessages: false,
      showAgentWaiting: false,
      isWaitingForFollowUp: false,
      isFormAlreadySubmitted: false,
    });
    // Reset form key to remount questions form
    setFormKey(prev => prev + 1);
    // Reset active tab to default
    setActiveTab('documents');

    // Call auth first to set new HTTP cookies before fetching questionnaire
    fetchVendorFormDetails(vendorToken as string)
  }, [vendorToken]);

  // Fetch vendor questionnaire after successful authentication
  // This fetches the detailed questionnaire status to determine which screen to show
  useEffect(() => {
    if (!data?.success || !data?.questionnaire_completion_status) {
      return;
    }

    // Only fetch once per token to prevent infinite loop
    if (hasFetchedQuestionnaireRef.current) {
      return;
    }

    // Capture current token at the time of making the request
    const requestToken = vendorToken;

    // Add delay to ensure HTTP cookies from auth call are set
    // The questionnaire API reads from HTTP cookies (not URL token)
    // Browser needs time to: receive response → parse Set-Cookie → update cookie storage
    // Old cookies persist until overwritten by server's Set-Cookie header
    const timer = setTimeout(() => {
      // Double-check we're still on the same token before making the request
      if (currentTokenRef.current !== requestToken) {
        return;
      }

      // Mark as fetched before making the request
      hasFetchedQuestionnaireRef.current = true;

      fetchVendorQuestionnaire(undefined, {
        onSuccess: (questionnaireResponse) => {
          if (currentTokenRef.current !== requestToken) {
            return;
          }

          const questionnaire = questionnaireResponse.questionnaire;
          const shouldShowChat = questionnaire.questionnaire_status === 'followup_questions_generated';
          const shouldShowAgentWaiting = questionnaire.questionnaire_status === 'completed';
          const shouldShowLoading = questionnaire.questionnaire_status === 'submitted';
          
          updateState({
            vendorQuestionnaire: questionnaire,
            shouldShowChatScreen: shouldShowChat,
            showAgentWaiting: shouldShowAgentWaiting,
            isWaitingForFollowUp: shouldShowLoading,
          });

          // If we should show chat, fetch the chat messages before hiding loader
          if (shouldShowChat && questionnaire.chat_id) {
            updateState({ isLoadingChatMessages: true });
            fetchChatMessages(
              { chatId: questionnaire.chat_id },
              {
                onSuccess: (chatResponse) => {
                  // Check token again before updating
                  if (currentTokenRef.current !== requestToken) {
                    return;
                  }
                  updateState({
                    chatMessages: chatResponse.messages || [],
                    isLoadingChatMessages: false,
                    isLoading: false, // Hide loader after chat messages are fetched
                  });
                },
                onError: (error) => {
                  if (currentTokenRef.current === requestToken) {
                    updateState({ 
                      isLoadingChatMessages: false,
                      isLoading: false, // Hide loader even on error
                    });
                  }
                }
              }
            );
          } else {
            // For other statuses (completed, submitted), hide loader immediately
            updateState({ isLoading: false });
          }
        },
        onError: (error) => {
          // If questionnaire fetch fails, hide loader anyway
          updateState({ isLoading: false });
        }
      });
    }, 500); // Reduced to 500ms since cookies should be set from first API call

    // Cleanup timer if token changes
    return () => clearTimeout(timer);
  }, [data?.success, data?.questionnaire_completion_status, vendorToken]);

  const { questionnaire_json_url } = data || {};

  const { mutate: submitVendorDetails, isIdle } = useSubmitVendorIntakeForm()
  const { mutate: submitQuestionnaireForm, isPending: isSubmittingQuestionnaire } = useSubmitQuestionnaireForm();

  // Document upload handlers
  const handleDocumentFilesChange = (documentId: string, files: UploadedFile[]) => {
    // Update local state first
    updateState({
      documentFiles: {
        ...documentFiles,
        [documentId]: files
      }
    });

    // Extract ALL valid signed URLs from current files state
    // This ensures we send the complete current state, not accumulated history
    const allValidSignedUrls = files
      .filter(file =>
        file.signedUrl &&
        file.signedUrl.length > 0 &&
        file.status === 'success'
      )
      .map(file => file.signedUrl as string);

    // Extract only NEW signed URLs (excluding existing files) to determine if we should trigger API call
    const newSignedUrls = files
      .filter(file =>
        file.signedUrl &&
        file.signedUrl.length > 0 &&
        file.status === 'success' &&
        !file.id.startsWith('existing-')
      )
      .map(file => file.signedUrl as string);

    // If we have new signed URLs from newly uploaded files, trigger autosave
    if (newSignedUrls.length > 0) {
      // Find the document to update
      const targetDocument = evidenceDocuments.find((doc: EvidenceDocument) => doc.id === documentId);

      if (targetDocument) {
        // Use complete set of valid URLs (replaces entire array to reflect current state)
        const updatedDocument: EvidenceDocument = {
          ...targetDocument,
          source_urls: allValidSignedUrls
        };

        // Set saving state for this document
        updateState({
          savingStates: {
            ...savingStates,
            [documentId]: true
          }
        });

        // Call autosave API
        uploadEvidence(
          { token: vendorToken, payload: { evidence_documents: [updatedDocument] } },
          {
            onSuccess: (response) => {
              if (response.success) {
                // Keep saving state true for minimum 2 seconds
                setTimeout(() => {
                  updateState({
                    evidenceDocuments: evidenceDocuments.map((doc: EvidenceDocument) =>
                      doc.id === documentId
                        ? response.evidence_documents.find((serverDoc: EvidenceDocument) => serverDoc.id === documentId) || doc
                        : doc
                    ),
                    savingStates: {
                      ...savingStates,
                      [documentId]: false
                    }
                  });
                }, 2000);
              }
            },
            onError: (error) => {

              updateState({
                savingStates: {
                  ...savingStates,
                  [documentId]: false
                }
              });
              addNotification({
                message: `Failed to save ${targetDocument.document_name}. Please try again.`,
                type: "error",
              });
            }
          }
        );
      }
    }
  };

  const handleUploadError = (documentId: string, hasError: boolean) => {
    updateState({
      uploadErrors: {
        ...uploadErrors,
        [documentId]: hasError
      }
    });
  };

  // Check if there are any upload errors
  const hasUploadErrors = Object.values(uploadErrors).some(error => error === true);

  // Check if required documents are uploaded
  const hasRequiredDocuments = () => {
    const requiredDocs = evidenceDocuments.filter((doc: EvidenceDocument) => doc.required);
    return requiredDocs.every((doc: EvidenceDocument) => {
      const files = documentFiles[doc.id] || [];
      return files.length > 0;
    });
  };

  // Check if there are any required items (documents or questions)
  const hasRequiredItems = () => {
    const requiredDocs = evidenceDocuments.filter((doc: EvidenceDocument) => doc.required);
    return requiredDocs.length > 0 || totalRequiredQuestions > 0;
  };

  // Calculate progress percentage based on required fields completion
  const calculateProgressPercentage = () => {
    // Count required evidence documents
    const requiredDocs = evidenceDocuments.filter((doc: EvidenceDocument) => doc.required);
    const completedRequiredDocs = requiredDocs.filter((doc: EvidenceDocument) => {
      const files = documentFiles[doc.id] || [];
      return files.length > 0;
    });

    // Total required items = required documents + required questions
    const totalRequiredItems = requiredDocs.length + totalRequiredQuestions;
    const completedRequiredItems = completedRequiredDocs.length + completedRequiredQuestions;

    // Calculate percentage
    if (totalRequiredItems === 0) return 0; // Return 0% when no data loaded yet
    return Math.round((completedRequiredItems / totalRequiredItems) * 100);
  };

  // Handler for saving state changes in questions form
  const handleSavingStateChange = useCallback((isSaving: boolean) => {
    updateState((currentState: any) => ({
      savingStates: {
        ...currentState.savingStates,
        questionnaire: isSaving
      }
    }));
  }, []);

  // Handler for form remount
  const handleFormRemount = useCallback(() => {
    setFormKey(prev => prev + 1);
  }, []);

  // Handler for questions progress updates
  const handleQuestionsProgressChange = useCallback((total: number, completed: number) => {
    updateState({
      totalRequiredQuestions: total,
      completedRequiredQuestions: completed
    });
  }, []);

  // Handle mark completed callback
  const handleMarkCompleted = useCallback(() => {
    updateState({ showAgentWaiting: true });
  }, []);

  // Handle immediate user message display
  const handleUserMessageSent = useCallback((message: ChatMessage) => {
    
    updateState((currentState: any) => {
      const currentMessages = currentState.chatMessages || [];
      
      // Simple approach - just add the message without duplicate check for now
      const newMessages = [...currentMessages, message];
      
      // Return the entire state object with explicit chatMessages update
      return {
        ...currentState,
        chatMessages: newMessages
      };
    });
  }, []);  // Custom delete handler for evidence files
  const handleEvidenceFileDelete = async (signedUrl: string, fileId: string) => {
    // Find which document this file belongs to
    let targetDocumentId: string | null = null;
    for (const [docId, files] of Object.entries(documentFiles)) {
      if ((files as UploadedFile[]).some((file: UploadedFile) => file.signedUrl === signedUrl)) {
        targetDocumentId = docId;
        break;
      }
    }

    if (!targetDocumentId) {
      return;
    }

    // Find the evidence document
    const targetDocument = evidenceDocuments.find((doc: EvidenceDocument) => doc.id === targetDocumentId);
    if (!targetDocument) {
      return;
    }

    // Create updated source_urls array without the deleted file
    const updatedSourceUrls = targetDocument.source_urls.filter((url: string) => url !== signedUrl);

    // Call PATCH API to update the evidence document
    patchEvidence(
      {
        token: vendorToken,
        evidenceId: targetDocumentId,
        sourceUrls: updatedSourceUrls
      },
      {
        onSuccess: (response: PatchEvidenceResponse) => {
          if (response.success) {
            // Update local evidence document state
            updateState({
              evidenceDocuments: evidenceDocuments.map((doc: EvidenceDocument) =>
                doc.id === targetDocumentId
                  ? { ...doc, source_urls: updatedSourceUrls }
                  : doc
              ),
              // Update local file state
              documentFiles: {
                ...documentFiles,
                [targetDocumentId]: (documentFiles[targetDocumentId] as UploadedFile[]).filter(
                  (file: UploadedFile) => file.signedUrl !== signedUrl
                )
              }
            });
          }
        },
        onError: (error) => {

          addNotification({
            message: "Failed to delete file. Please try again.",
            type: "error",
          });
        }
      }
    );
  };

  // For loading states, show the UI structure with loader in content area

  if (isSessionExpired) {
    return <ThirdPartyFormExpiredScreen contact_email={data.data?.contact_email} />
  }

  // Show VendorEmailPlaceHolder when form is already submitted
  if (isFormAlreadySubmitted) {
    return (
      <ThirdPartyVendorLayout>
        <VendorEmailPlaceHolder organizationName={data?.assessor_name || data?.organization_name} />
      </ThirdPartyVendorLayout>
    );
  }

  // Show VendorEmailPlaceHolder screen when questionnaire is completed
  if (showAgentWaiting || (vendorQuestionnaire?.questionnaire_status === 'completed')) {
    return (
      <ThirdPartyVendorLayout>
        <VendorEmailPlaceHolder organizationName={data?.assessor_name || data?.organization_name} />
      </ThirdPartyVendorLayout>
    );
  }

  // Show AssessmentLoadingScreen when waiting for follow-up questions or when questionnaire status is 'submitted'
  if (isWaitingForFollowUp || vendorQuestionnaire?.questionnaire_status === 'submitted') {

    return <AssessmentLoadingScreen />;
  }

  if (isAlreadySubmitted) {
    return (
      <SuccessScreen
        token={vendorToken}
        sessionId={vendorQuestionnaire?.chat_id}
        chatMessages={chatMessages}
        isLoadingMessages={isLoadingChatMessages}
        onMarkCompleted={handleMarkCompleted}
        onUserMessageSent={handleUserMessageSent}
      />
    )
  }

  // Show chat screen if questionnaire status is 'followup_questions_generated'
  // Check this BEFORE the isLoading check to prevent flashing the documents screen
  if (shouldShowChatScreen && vendorQuestionnaire) {
    // If still loading chat messages, show loader
    if (isLoading || isLoadingChatMessages) {
      return (
        <ThirdPartyVendorLayout>
          <div className="w-full h-full flex items-center justify-center">
            <LoadingSpinner variant="centered" />
          </div>
        </ThirdPartyVendorLayout>
      );
    }
    
    return (
      <SuccessScreen 
        token={vendorToken} 
        chatMessages={chatMessages}
        isLoadingMessages={isLoadingChatMessages}
        sessionId={vendorQuestionnaire.chat_id}
        onMarkCompleted={handleMarkCompleted}
        onUserMessageSent={handleUserMessageSent}
      />
    )
  }

  // Show loader during initial authentication and data fetching
  // OR if we haven't received questionnaire_completion_status yet
  if (isLoading || !data?.questionnaire_completion_status) {
    return (
      <ThirdPartyVendorLayout>
        <div className="w-full h-full flex items-center justify-center">
          <LoadingSpinner variant="centered" />
        </div>
      </ThirdPartyVendorLayout>
    );
  }

  const confirmSubmission = () => {
    submitQuestionnaireForm(
      {
        token: vendorToken as string,
        payload: {
          questionnaire_url: data.questionnaire_json_url || ''
        }
      },
      {
        onSuccess: (response: any) => {
          if (response.success) {

            // Set waiting state to show loading screen
            updateState({
              isAlreadySubmitted: true,
              isWaitingForFollowUp: true
            });
          } else {
            addNotification({
              message: response.message || "Failed to submit questionnaire.",
              type: "error",
            });
          }
        },
        onSettled: () => {
          toggleField("isConfirmModalOpen");
        },
        onError: (error: any) => {
          addNotification({
            message: error?.response?.data?.error_message || "Failed to submit questionnaire. Please try again.",
            type: "error",
          });
        }
      },
    );
  }

  const handleSubmitVendorData = () => {
    // Check if all required fields are completed (100% progress or nothing required)
    const progressPercentage = calculateProgressPercentage();
    if (progressPercentage < 100 && progressPercentage > 0) {
      addNotification({
        message: "Please complete all required fields and documents to submit.",
        type: "error",
      });
      return;
    }

    // Check for upload errors
    if (hasUploadErrors) {
      addNotification({
        message: "Please fix file upload errors before submitting.",
        type: "error",
      });
      return;
    }

    toggleField("isConfirmModalOpen");
  }




  return (
    <ThirdPartyVendorLayout>
      <div className="w-full h-full flex flex-col">
        {/* Breadcrumb Navigation */}
        <div className="flex items-center pb-4 justify-between min-h-[44px] max-h-[44px] px-6">
          <div className="flex gap-4 items-center">
            <ZBreadCrumbs
              items={[
                { title: "Vendor Due Diligence", href: "" }
              ]}
            />
          </div>
          <div className="flex gap-2">
            <ZButton
              color="primary"
              className="text-xs font-medium h-7 px-3"
              onClick={handleSubmitVendorData}
              isDisabled={isLoading || isEvidenceLoading || !isIdle || hasUploadErrors || (hasRequiredItems() && calculateProgressPercentage() < 100)}
            >
              Submit
            </ZButton>
          </div>
        </div>

        {/* Content Container with rounded corners */}
        <div className="flex-1 flex flex-col gap-0.5 bg-[#F4F4F5] rounded-lg overflow-hidden">
          {/* Header with tabs and progress */}
          <div className="bg-white rounded-t-lg">
            <ThirdPartyHeader
              activeTab={activeTab}
              onTabChange={setActiveTab}
              progressPercentage={calculateProgressPercentage()}
              isSaving={Object.values(savingStates).some(isSaving => isSaving)}
            />
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden bg-white">
            <div className="h-full w-full bg-white flex flex-col">
              {/* Scrollable Content Area */}
              <div className="flex-1 px-6 pt-4 pb-24">
                {isLoading || isEvidenceLoading ? (
                  // Show loader in content area during initial load
                  <div className="w-full h-full flex items-center justify-center">
                    <LoadingSpinner variant="centered" />
                  </div>
                ) : (
                  <>
                    {/* Keep both components mounted to preserve state, show/hide with CSS */}
                    <div className={activeTab === 'documents' ? 'block' : 'hidden'}>
                      <DocumentUploadGrid
                        evidenceDocuments={evidenceDocuments}
                        documentFiles={documentFiles}
                        onDocumentFilesChange={handleDocumentFilesChange}
                        onUploadError={handleUploadError}
                        savingStates={savingStates}
                        customDeleteHandler={handleEvidenceFileDelete}
                      />
                      </div>
                      <div className={activeTab === 'questions' ? 'block' : 'hidden'}>
                        <QuestionsForm
                          key={formKey}
                          questionnaireUrl={questionnaire_json_url || null}
                          token={vendorToken}
                          questionnaireExcelUrl={data.questionnaire_excel_url}
                          onSavingStateChange={handleSavingStateChange}
                          onPaginationChange={setQuestionsPagination}
                          onProgressChange={handleQuestionsProgressChange}
                          onFormRemount={handleFormRemount}
                        />
                      </div>
                    </>
                )}
              </div>
              
              {/* Sticky Pagination - Only for Questions Tab */}
              {activeTab === 'questions' && questionsPagination && (
                <div className="sticky bottom-0 z-20 border-t border-[#E4E4E7] bg-white">
                  {questionsPagination}
                </div>
              )}
            </div>
          </div>
        </div>

        {isConfirmModalOpen && (
          <SubmitQuestionnaireModal
            isOpen
            onClose={() => toggleField("isConfirmModalOpen")}
            onConfirm={confirmSubmission}
            isLoading={isSubmittingQuestionnaire}
          />
        )}
      </div>
    </ThirdPartyVendorLayout>
  );
}


export default VendorIntakeForm;